import { redirect } from "@tanstack/react-router";
import { tokenStorage } from "./tokenStorage";
/* eslint-disable @typescript-eslint/only-throw-error -- TanStack Router requires throwing redirect() */
import { legalService } from "@/api/legal";
import { TRole, USER_ROLES } from "@/api/user/user.types";
import { useStore } from "@/store";
import { getReturnPathFromLocation, type TRouteLocationLike } from "./authUtils";

/**
 * Route Guard Utilities for TanStack Router
 *
 * These guards are designed for use in `beforeLoad` hooks and follow a clear route structure:
 *
 * Route Structure:
 * ├── /_public/*  → No guard (accessible by everyone)
 * ├── /auth/*     → requireGuest (guest-only, redirects authenticated users to /app)
 * └── /app/*      → requireAuth (auth-required, redirects guests to /auth/login)
 *
 * Guards use token storage (localStorage) as the single source of truth for auth state.
 * This ensures consistency with the API layer which also reads tokens from the same storage.
 */

/**
 * Guard: Require authenticated user
 *
 * Redirects to login page if user is not authenticated.
 * Apply to protected route layouts (e.g., /app.tsx) so all child routes inherit protection.
 *
 * @example
 * // In /routes/app.tsx
 * export const Route = createFileRoute("/app")({
 *   beforeLoad: () => requireAuth(),
 *   component: AppLayout,
 * });
 */
export function requireAuth(location?: TRouteLocationLike): void {
  // Use cookies as single source of truth to prevent desync with API layer
  const accessToken = tokenStorage.getAccessToken();
  if (!accessToken) {
    const from = getReturnPathFromLocation(location);
    throw redirect({
      to: "/auth/login",
      search: { from: from ?? undefined },
    });
  }
}

/**
 * Guard: Require verified user (email OR phone)
 *
 * Ensures the authenticated user has verified at least one method.
 * If BOTH email_verified and phone_verified are false, redirects to /auth/verify-method.
 *
 * Intended for use in /app layout beforeLoad, after requireAuth().
 */
export async function requireVerified(location?: TRouteLocationLike): Promise<void> {
  // Must be authenticated first
  requireAuth(location);

  const state = useStore.getState();
  if (!state.profile) {
    await state.fetchProfile({ force: true });
  }

  const profile = useStore.getState().profile;
  if (profile && !profile.email_verified && !profile.phone_verified) {
    throw redirect({ to: "/auth/verify-method" });
  }
}

/**
 * Guard: Require current legal document acceptances
 *
 * Redirects to accept-legal if the user has pending required documents.
 * Intended for use in /app layout beforeLoad, after requireAuth() and requireVerified().
 */
export async function requireLegalAccepted(location?: TRouteLocationLike): Promise<void> {
  requireAuth(location);

  const { data, error } = await legalService.getStatus();
  if (error instanceof TypeError) {
    throw error;
  }
  if (data && !data.data.all_required_accepted) {
    const from = getReturnPathFromLocation(location);
    throw redirect({
      to: "/auth/accept-legal",
      search: { from: from ?? undefined },
    });
  }
}

/**
 * Guard: Require guest (not authenticated)
 *
 * Redirects to /app if user is already authenticated.
 * Apply to auth route layout (e.g., /auth.tsx) for guest-only pages like login/signup.
 *
 * Note: For truly public routes accessible by everyone, use no guard (see /_public layout).
 *
 * @param options - Optional configuration
 * @param options.allowedRoutes - Routes that should be accessible even with a token (e.g., during 2FA flow)
 * @param options.pathname - Target pathname (prefer passing TanStack Router's `location.pathname` from beforeLoad)
 *
 * @example
 * // In /routes/auth.tsx
 * export const Route = createFileRoute("/auth")({
 *   beforeLoad: () => requireGuest(),
 *   component: AuthLayout,
 * });
 */
export function requireGuest(options?: { allowedRoutes?: string[]; pathname?: string }): void {
  // Use cookies as single source of truth to prevent desync with API layer
  const accessToken = tokenStorage.getAccessToken();
  if (accessToken) {
    // Check if current route is in allowed routes (for 2FA flow)
    const allowedRoutes = options?.allowedRoutes ?? [];
    const pathname = options?.pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");

    // IMPORTANT: Use the router's target pathname (passed in) rather than window.location,
    // because beforeLoad runs before the URL is committed during client-side navigation.
    if (allowedRoutes.some((route) => pathname.startsWith(route))) {
      return; // Allow access to this route even with token
    }
    throw redirect({ to: "/app" });
  }
}

/**
 * Guard: Require company exists
 * Fetches companies and validates that the specified company exists.
 * Redirects to /app if company not found.
 * If onboarding is not complete, redirects to onboarding.
 * @param companyId - The company ID to validate
 * @param options - Options to control behavior
 * @returns The company object if found
 */
export async function requireCompany(
  companyId: string,
  options?: { skipOnboardingCheck?: boolean; forceRefresh?: boolean }
) {
  const { fetchCompanies, getCompanyById } = useStore.getState();
  await fetchCompanies({ force: options?.forceRefresh ?? false });
  const company = getCompanyById(companyId);

  if (!company) {
    throw redirect({ to: "/app" });
  }

  // If onboarding is not complete and we're not skipping the check, redirect to onboarding
  if (!options?.skipOnboardingCheck && !company.onboarding_completed) {
    throw redirect({
      to: "/app/companies/$company_id/onboarding",
      params: { company_id: companyId },
    });
  }

  return company;
}

/**
 * Guard: Require first-time onboarding
 *
 * Redirects to /app if the user already has at least one completed company.
 * Apply to /app/onboarding so returning users can't re-enter the initial onboarding flow
 * via direct URL navigation. Adding a second company is a deliberate dashboard action
 * (sidebar "Add new company"), which navigates here with `?new=true` to bypass the guard.
 */
export async function requireFirstTimeOnboarding(options?: { allowNewCompany?: boolean }): Promise<void> {
  if (options?.allowNewCompany) {
    return;
  }
  const state = useStore.getState();
  if (!state.companies) {
    await state.fetchCompanies({ force: false });
  }
  const { companies } = useStore.getState();
  if (companies?.some((c) => c.onboarding_completed)) {
    throw redirect({ to: "/app" });
  }
}

/**
 * Guard: Handle onboarding status redirect
 * Checks company onboarding status and redirects accordingly:
 * - If onboarding complete → redirect to summary page (handles all states: processing, failed, ready)
 * - If onboarding not complete → allow access to continue onboarding
 * @param companyId - The company ID to check
 * @returns The company object if onboarding is not complete
 */
export async function handleOnboardingRedirect(companyId: string) {
  // Skip onboarding check since we're already in the onboarding route
  // Force refresh to ensure we have the latest company data (important for newly created companies)
  const company = await requireCompany(companyId, { skipOnboardingCheck: true, forceRefresh: true });

  if (company.onboarding_completed) {
    // Redirect to summary page - it handles all states (processing, failed, ready)
    throw redirect({
      to: "/app/companies/$company_id/onboarding/summary",
      params: { company_id: companyId },
    });
  }

  // Onboarding not complete - allow access to continue
  return company;
}

/**
 * Utility: Check if user has admin role
 * Checks if the provided roles array contains a role with name matching USER_ROLES.admin
 * @param roles - Array of roles to check
 * @returns true if user has admin role, false otherwise
 */
const ADMIN_ROLES = new Set<string>([
  USER_ROLES.admin,
  USER_ROLES.super_admin,
  USER_ROLES.moderator,
  USER_ROLES.user_manager,
]);

export function hasAdminRole(roles: TRole[] | null | undefined): boolean {
  if (!roles || roles.length === 0) {
    return false;
  }
  return roles.some((role) => ADMIN_ROLES.has(role.name));
}

export function hasReviewerRole(roles: TRole[] | null | undefined): boolean {
  if (!roles || roles.length === 0) {
    return false;
  }
  return roles.some((role) => role.name === USER_ROLES.reviewer);
}

/**
 * Utility: Check if user can access the Investor Onboarding Job Pool.
 * True for the `sales` role or any admin-tier role (admins process escalated jobs).
 */
export function hasSalesRole(roles: TRole[] | null | undefined): boolean {
  if (!roles || roles.length === 0) {
    return false;
  }
  return roles.some((role) => role.name === USER_ROLES.sales) || hasAdminRole(roles);
}

/**
 * Guard: Require admin role
 *
 * Redirects to /app if user is not authenticated or not an admin.
 * First checks authentication, then ensures roles are loaded, then verifies admin role.
 * Checks the roles array for a role with name matching USER_ROLES.admin.
 * Apply to admin route layouts (e.g., /app/admin.tsx) so all child routes inherit protection.
 *
 * @example
 * // In /routes/app/admin.tsx
 * export const Route = createFileRoute("/app/admin")({
 *   beforeLoad: async () => { await requireAdmin(); },
 *   component: AdminLayout,
 * });
 */
export async function requireAdmin(location?: TRouteLocationLike): Promise<void> {
  requireAuth(location);

  const state = useStore.getState();
  if (!state.roles) {
    await state.fetchRoles({ force: true });
  }

  const roles = useStore.getState().roles;
  if (!hasAdminRole(roles)) {
    throw redirect({ to: "/app" });
  }
}

export async function requireReviewer(location?: TRouteLocationLike): Promise<void> {
  requireAuth(location);

  const state = useStore.getState();
  if (!state.roles) {
    await state.fetchRoles({ force: true });
  }

  const roles = useStore.getState().roles;
  if (!hasReviewerRole(roles)) {
    throw redirect({ to: "/app" });
  }
}

/**
 * Guard: Require sales (or admin-tier) role
 *
 * Protects the Investor Onboarding Job Pool routes (/app/sales/*).
 */
export async function requireSales(location?: TRouteLocationLike): Promise<void> {
  requireAuth(location);

  const state = useStore.getState();
  if (!state.roles) {
    await state.fetchRoles({ force: true });
  }

  const roles = useStore.getState().roles;
  if (!hasSalesRole(roles)) {
    throw redirect({ to: "/app" });
  }
}
